// Guarda lo pendiente cuando el service worker avisa que va a recargar.
//
// Por qué existe (17-ago-2026, Planner #980): el service worker "suicida"
// (`public/service-worker.js`) cura los navegadores que quedaron con un bundle
// viejo cacheado, y para eso tiene que recargar la pestaña. Lo hacía sin
// avisar: a un médico se le recargó la app a las 08:22 en pleno turno y lo
// reportó como "se cayó la app". La recarga no se puede evitar —sin ella el
// navegador enfermo no se cura— pero sí se puede llegar preparado.
//
// El texto que el médico tipea YA está a salvo: `stageDraft` lo escribe en
// localStorage en cada tecla. Lo que se perdía era lo que todavía no había
// salido a la red: la cola de telemetría y las celdas sucias sin PATCH.
//
// La ventana de gracia la fija el SW (800 ms) y NO espera confirmación: un
// navegador enfermo corre un bundle viejo que no conoce este mensaje y nunca
// contestaría. Esto es best-effort dentro de esa ventana, nunca un bloqueo.

import { flushActivity, logActivity } from "./activity-log";

const MENSAJE = "SW_SUICIDE_RELOAD";

// Callbacks que registran las vistas para empujar su estado sucio (ej. el
// Turno manda los PATCH de las celdas que aún no se guardaron). Se llaman en
// orden de registro, cada uno protegido: si uno explota, los demás corren.
const pendientes = new Set();

/**
 * Registra un callback a ejecutar justo antes de que el SW recargue.
 * Devuelve la función para desregistrarlo (usar en el cleanup del efecto).
 *
 * @param {() => void | Promise<void>} fn
 */
export function onBeforeSwReload(fn) {
  if (typeof fn !== "function") return () => {};
  pendientes.add(fn);
  return () => pendientes.delete(fn);
}

// Lo que se registra por defecto: la recarga del service worker suicida.
const EVENTO_SW = { type: "sw_reload", meta: { reason: "sw_suicide" } };

/**
 * Guarda lo pendiente antes de una recarga: registra el evento, corre los
 * callbacks de `onBeforeSwReload` y vacía la telemetría. Nunca lanza.
 *
 * Exportado para que la recarga por versión nueva (#1451,
 * `helpers/version-nueva.js`) reuse exactamente los mismos pasos. Sin
 * argumento se comporta como siempre (evento `sw_reload`).
 *
 * @param {{ type: string, meta?: object } | null} [evento] qué registrar
 *   primero; `null` = no registrar nada (el que llama ya lo registró).
 */
export async function guardarLoPendiente(evento = EVENTO_SW) {
  // 1. Avisar que esto pasó. Es el único registro de cuántos navegadores se
  //    curaron: sin este evento no hay forma de saber cuántos quedan enfermos
  //    ni de correlacionar un "se cayó la app" con su causa real.
  if (evento) {
    try {
      logActivity(evento.type, { module: "app", meta: evento.meta || {} });
    } catch (_) {}
  }

  // 2. Empujar el estado sucio de las vistas (celdas del turno sin PATCH).
  for (const fn of pendientes) {
    try {
      await fn();
    } catch (_) {
      /* una vista que falla no puede impedir que las otras guarden */
    }
  }

  // 3. Vaciar la cola de telemetría. Va al final para que se lleve también el
  //    evento del paso 1 y lo que hayan encolado los callbacks. Usa fetch con
  //    keepalive, así que sobrevive a la recarga.
  try {
    flushActivity();
  } catch (_) {}
}

let instalado = false;

/**
 * Cablea el listener. Idempotente: llamarlo dos veces no duplica handlers.
 * Se invoca una vez al arrancar la app.
 */
export function installSwReloadGuard() {
  if (instalado) return;
  instalado = true;
  try {
    if (typeof navigator === "undefined" || !navigator.serviceWorker) return;
    navigator.serviceWorker.addEventListener("message", (event) => {
      if (event?.data?.type !== MENSAJE) return;
      guardarLoPendiente();
    });
  } catch (_) {
    // Sin service worker (o bloqueado por el navegador) no hay nada que hacer:
    // la app funciona igual, solo que sin aviso previo.
  }
}

export default installSwReloadGuard;
